/**
 * Discussion Comment Service
 *
 * Story BL-039.2: Wire DiscussionDetailPage Comments
 * Task 3: Create discussionCommentService.ts
 *
 * REST client for the oracle-bridge discussion comments API.
 * All operations use `credentials: 'include'` for cookie-based auth.
 *
 * CRITICAL: Uses `getOracleBridgeUrl()` NOT `getApiBaseUrl()`.
 *
 * @module services/discussionCommentService
 */

import { authenticatedFetch } from '@hello-world-dao/auth';
import { getOracleBridgeUrl, readErrorBody, selectServerMessage } from './apiHelpers';

// =============================================================================
// Types (Task 3.7)
// =============================================================================

/**
 * Discussion comment as returned by oracle-bridge
 */
export interface DiscussionComment {
  id: string;
  discussionId: string;
  parentId: string | null;
  authorUserId: string;
  authorDisplayName: string;
  content: string;
  isDeleted: boolean;
  upvotes: number;
  downvotes: number;
  score: number;
  userVote: -1 | 0 | 1;
  createdAt: string;
  updatedAt: string;
  replies?: DiscussionComment[];
}

/**
 * Response from listing discussion comments
 */
export interface ListDiscussionCommentsResponse {
  comments: (DiscussionComment & { replies: DiscussionComment[] })[];
  total_top_level: number;
}

/**
 * Vote result from oracle-bridge
 */
export interface DiscussionVoteResult {
  upvotes: number;
  downvotes: number;
  score: number;
  userVote: -1 | 0 | 1;
}

/**
 * API error type for discussion comment operations
 */
export interface DiscussionCommentApiError {
  code: 'UNAUTHENTICATED' | 'FORBIDDEN' | 'NOT_FOUND' | 'VALIDATION_ERROR' | 'SERVER_ERROR';
  status?: number;
  message?: string;
}

// =============================================================================
// Error Handling
// =============================================================================

/**
 * Map an error `Response` from the discussion-comments API.
 *
 * bl-1153-1: the body is read EXACTLY ONCE and every branch prefers the server's
 * own sentence. Before this story only the 400 branch read the body, so 401/403/
 * 404/5xx returned no `message` field at all.
 *
 * This route's server is the INVERTED case in the fleet: oracle-bridge
 * `src/routes/discussion-comments.ts` puts bare HTTP reason phrases in `error`
 * (`'Bad Request'`, `'Forbidden'`) and never a machine code, so the human text is
 * only ever in `message`. `selectServerMessage` is correct here precisely because
 * it suppresses bare reason phrases — a hand-rolled `data.message || data.error`
 * would render the member the word "Forbidden".
 *
 * `message` is left UNDEFINED when the server supplied no human text, exactly as
 * before — the render sites keep their curated per-context copy and this mapper
 * only upgrades it when the server actually said something.
 *
 * Codes and statuses are byte-identical to the pre-bl-1153-1 mapping;
 * `DiscussionDetailPage` routes on `code`.
 */
async function mapError(res: Response): Promise<DiscussionCommentApiError> {
  const data = await readErrorBody(res);
  const serverMessage = selectServerMessage(data);

  if (res.status === 401) {
    return { code: 'UNAUTHENTICATED', message: serverMessage };
  }
  if (res.status === 403) {
    return { code: 'FORBIDDEN', status: 403, message: serverMessage };
  }
  if (res.status === 404) {
    return { code: 'NOT_FOUND', status: 404, message: serverMessage };
  }
  if (res.status === 400) {
    // The only branch that carried a canned string before bl-1153-1; it stays.
    return { code: 'VALIDATION_ERROR', status: 400, message: serverMessage ?? 'Validation failed' };
  }
  return { code: 'SERVER_ERROR', status: res.status, message: serverMessage };
}

// =============================================================================
// API Functions
// =============================================================================

/**
 * List comments for a discussion (public endpoint with optional auth).
 *
 * @param discussionId - Discussion UUID
 * @returns Comments with nested replies and total count
 * @throws DiscussionCommentApiError on failure
 */
export async function listDiscussionComments(
  discussionId: string,
): Promise<ListDiscussionCommentsResponse> {
  const base = getOracleBridgeUrl();
  const res = await authenticatedFetch(
    `${base}/api/discussions/${encodeURIComponent(discussionId)}/comments`,
    {},
  );
  if (!res.ok) throw await mapError(res);
  return res.json();
}

/**
 * Post a new comment on a discussion (authenticated).
 *
 * @param discussionId - Discussion UUID
 * @param content - Comment content
 * @param parentId - Optional parent comment UUID for replies
 * @returns Created comment
 * @throws DiscussionCommentApiError on failure
 */
export async function postDiscussionComment(
  discussionId: string,
  content: string,
  parentId?: string,
): Promise<DiscussionComment> {
  const base = getOracleBridgeUrl();
  const res = await authenticatedFetch(
    `${base}/api/discussions/${encodeURIComponent(discussionId)}/comments`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content, ...(parentId ? { parentId } : {}) }),
    },
  );
  if (!res.ok) throw await mapError(res);
  return res.json();
}

/**
 * Edit an existing discussion comment (authenticated, author only).
 *
 * @param discussionId - Discussion UUID
 * @param commentId - Comment UUID
 * @param content - Updated content
 * @returns Updated comment
 * @throws DiscussionCommentApiError on failure
 */
export async function editDiscussionComment(
  discussionId: string,
  commentId: string,
  content: string,
): Promise<DiscussionComment> {
  const base = getOracleBridgeUrl();
  const res = await authenticatedFetch(
    `${base}/api/discussions/${encodeURIComponent(discussionId)}/comments/${encodeURIComponent(commentId)}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content }),
    },
  );
  if (!res.ok) throw await mapError(res);
  return res.json();
}

/**
 * Delete a discussion comment (authenticated, author or admin/moderator).
 *
 * @param discussionId - Discussion UUID
 * @param commentId - Comment UUID
 * @throws DiscussionCommentApiError on failure
 */
export async function deleteDiscussionComment(
  discussionId: string,
  commentId: string,
): Promise<void> {
  const base = getOracleBridgeUrl();
  const res = await authenticatedFetch(
    `${base}/api/discussions/${encodeURIComponent(discussionId)}/comments/${encodeURIComponent(commentId)}`,
    {
      method: 'DELETE',
    },
  );
  if (!res.ok) throw await mapError(res);
}

/**
 * Vote on a discussion comment (authenticated).
 *
 * @param discussionId - Discussion UUID
 * @param commentId - Comment UUID
 * @param vote - 1 for upvote, -1 for downvote (same vote toggles off)
 * @returns Updated vote counts
 * @throws DiscussionCommentApiError on failure
 */
export async function voteDiscussionComment(
  discussionId: string,
  commentId: string,
  vote: 1 | -1,
): Promise<DiscussionVoteResult> {
  const base = getOracleBridgeUrl();
  const res = await authenticatedFetch(
    `${base}/api/discussions/${encodeURIComponent(discussionId)}/comments/${encodeURIComponent(commentId)}/vote`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ vote }),
    },
  );
  if (!res.ok) throw await mapError(res);
  return res.json();
}
